import { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { Pin, PinOff, Pencil, Trash2, History, Loader2, Plus, X } from "lucide-react";

import { api } from "@/lib/api";
import { formatApiError } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
    AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { relativeTime } from "@/lib/constants";

const CATEGORIES = [
    { value: "general", label: "General" },
    { value: "tax_position", label: "Tax Position" },
    { value: "withholding", label: "Withholding Tax" },
    { value: "reminder", label: "Reminder" },
    { value: "compliance", label: "Compliance" },
];

const CATEGORY_STYLES = {
    general: "bg-[#E8E8E4] text-[#0F1115] border-[#D4D4CF]",
    tax_position: "bg-[#E6F0FA] text-[#1E3A5F] border-[#C6DBF0]",
    withholding: "bg-[#FBE8E8] text-[#7A1F1F] border-[#F1C8C8]",
    reminder: "bg-[#FFF4D6] text-[#6B4F00] border-[#F0E0A3]",
    compliance: "bg-[#E8F3EC] text-[#1F5235] border-[#C8E0D1]",
};

function labelForCategory(v) {
    return CATEGORIES.find((c) => c.value === v)?.label || "General";
}

function fmtTime(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return d.toLocaleString(undefined, {
            year: "numeric", month: "short", day: "2-digit",
            hour: "2-digit", minute: "2-digit",
        });
    } catch {
        return iso;
    }
}

export default function ClientNotesTab({ clientId }) {
    const [notes, setNotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all");
    const [showAdd, setShowAdd] = useState(false);
    const [editing, setEditing] = useState(null);
    const [historyFor, setHistoryFor] = useState(null);

    async function load() {
        setLoading(true);
        try {
            const r = await api.get(`/clients/${clientId}/notes`);
            setNotes(r.data || []);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clientId]);

    const filtered = useMemo(() => {
        if (filter === "all") return notes;
        return notes.filter((n) => n.category === filter);
    }, [notes, filter]);

    async function togglePin(note) {
        try {
            await api.patch(`/clients/${clientId}/notes/${note.id}`, { pinned: !note.pinned });
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }

    async function del(note) {
        try {
            await api.delete(`/clients/${clientId}/notes/${note.id}`);
            toast.success("Note deleted");
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }

    return (
        <div className="space-y-4" data-testid="client-notes-tab">
            <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                    <div className="co-label">Filter</div>
                    <Select value={filter} onValueChange={setFilter}>
                        <SelectTrigger className="h-9 w-[200px]" data-testid="notes-filter">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All categories</SelectItem>
                            {CATEGORIES.map((c) => (
                                <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <Button
                    onClick={() => setShowAdd(true)}
                    className="co-btn-primary"
                    data-testid="notes-add-btn"
                >
                    <Plus className="h-4 w-4" /> Add note
                </Button>
            </div>

            {loading ? (
                <div className="co-card co-card-pad flex items-center justify-center gap-2 text-sm text-[#5E636E]">
                    <Loader2 className="h-4 w-4 animate-spin" /> Loading notes…
                </div>
            ) : filtered.length === 0 ? (
                <div className="co-card co-card-pad text-sm text-[#5E636E]">
                    {notes.length === 0
                        ? "No notes yet. Add the first note to start building the audit trail."
                        : "No notes match this filter."}
                </div>
            ) : (
                <ul className="space-y-3" data-testid="notes-list">
                    {filtered.map((n) => (
                        <li
                            key={n.id}
                            className="co-card co-card-pad"
                            data-testid={`note-${n.id}`}
                        >
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <Badge
                                            variant="outline"
                                            className={`${CATEGORY_STYLES[n.category] || CATEGORY_STYLES.general} border`}
                                        >
                                            {labelForCategory(n.category)}
                                        </Badge>
                                        {n.pinned && (
                                            <Badge className="bg-[#0F1115] text-white border-[#0F1115]">
                                                <Pin className="h-3 w-3 mr-1" /> Pinned
                                            </Badge>
                                        )}
                                        {n.revisions?.length > 0 && (
                                            <button
                                                type="button"
                                                onClick={() => setHistoryFor(n)}
                                                className="text-[11px] text-[#5E636E] hover:text-[#0F1115] flex items-center gap-1"
                                                data-testid={`note-history-${n.id}`}
                                            >
                                                <History className="h-3 w-3" /> {n.revisions.length} edit{n.revisions.length === 1 ? "" : "s"}
                                            </button>
                                        )}
                                    </div>
                                    <p className="mt-2 text-sm whitespace-pre-wrap">{n.body}</p>
                                    <div className="mt-2 text-[11px] text-[#5E636E]">
                                        Created by <span className="font-medium text-[#0F1115]">{n.created_by_name || "—"}</span> on {fmtTime(n.created_at)}
                                        {n.updated_at && n.updated_at !== n.created_at && (
                                            <>
                                                {" · "}Edited by <span className="font-medium text-[#0F1115]">{n.updated_by_name || "—"}</span> {relativeTime(n.updated_at)}
                                            </>
                                        )}
                                    </div>
                                </div>
                                <div className="flex items-center gap-1 shrink-0">
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => togglePin(n)}
                                        title={n.pinned ? "Unpin" : "Pin"}
                                        data-testid={`note-pin-${n.id}`}
                                    >
                                        {n.pinned ? <PinOff className="h-4 w-4" /> : <Pin className="h-4 w-4" />}
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => setEditing(n)}
                                        title="Edit"
                                        data-testid={`note-edit-${n.id}`}
                                    >
                                        <Pencil className="h-4 w-4" />
                                    </Button>
                                    <AlertDialog>
                                        <AlertDialogTrigger asChild>
                                            <Button variant="ghost" size="icon" title="Delete" data-testid={`note-delete-${n.id}`}>
                                                <Trash2 className="h-4 w-4 text-[#B91C1C]" />
                                            </Button>
                                        </AlertDialogTrigger>
                                        <AlertDialogContent>
                                            <AlertDialogHeader>
                                                <AlertDialogTitle>Delete this note?</AlertDialogTitle>
                                                <AlertDialogDescription>
                                                    This removes the note and its edit history permanently.
                                                </AlertDialogDescription>
                                            </AlertDialogHeader>
                                            <AlertDialogFooter>
                                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                                <AlertDialogAction
                                                    onClick={() => del(n)}
                                                    data-testid={`note-delete-confirm-${n.id}`}
                                                >Delete</AlertDialogAction>
                                            </AlertDialogFooter>
                                        </AlertDialogContent>
                                    </AlertDialog>
                                </div>
                            </div>
                        </li>
                    ))}
                </ul>
            )}

            {showAdd && (
                <NoteEditor
                    clientId={clientId}
                    mode="create"
                    onClose={() => setShowAdd(false)}
                    onSaved={async () => { setShowAdd(false); await load(); }}
                />
            )}
            {editing && (
                <NoteEditor
                    clientId={clientId}
                    mode="edit"
                    note={editing}
                    onClose={() => setEditing(null)}
                    onSaved={async () => { setEditing(null); await load(); }}
                />
            )}
            {historyFor && (
                <HistoryDialog note={historyFor} onClose={() => setHistoryFor(null)} />
            )}
        </div>
    );
}

function NoteEditor({ clientId, mode, note, onClose, onSaved }) {
    const [body, setBody] = useState(note?.body || "");
    const [category, setCategory] = useState(note?.category || "general");
    const [pinned, setPinned] = useState(!!note?.pinned);
    const [saving, setSaving] = useState(false);

    async function save() {
        if (!body.trim()) {
            toast.error("Note body cannot be empty");
            return;
        }
        setSaving(true);
        try {
            if (mode === "create") {
                await api.post(`/clients/${clientId}/notes`, { body: body.trim(), category, pinned });
                toast.success("Note added");
            } else {
                await api.patch(`/clients/${clientId}/notes/${note.id}`, {
                    body: body.trim(), category, pinned,
                });
                toast.success("Note updated");
            }
            await onSaved();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSaving(false);
        }
    }

    return (
        <Dialog open onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="sm:max-w-[560px]" data-testid="note-editor">
                <DialogHeader>
                    <DialogTitle>{mode === "create" ? "Add note" : "Edit note"}</DialogTitle>
                    <DialogDescription>
                        {mode === "edit"
                            ? "Changes are saved with your name and timestamp in the audit trail."
                            : "Captured against this client with your name and the current time."}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-3">
                    <div>
                        <div className="co-label mb-1">Category</div>
                        <Select value={category} onValueChange={setCategory}>
                            <SelectTrigger data-testid="note-editor-category">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {CATEGORIES.map((c) => (
                                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <div className="co-label mb-1">Note</div>
                        <Textarea
                            value={body}
                            onChange={(e) => setBody(e.target.value)}
                            rows={6}
                            placeholder="e.g. Client has agreed to pay withholding taxes monthly. Remind on 15th each month."
                            data-testid="note-editor-body"
                        />
                    </div>
                    <label className="flex items-center gap-2 text-sm">
                        <input
                            type="checkbox"
                            checked={pinned}
                            onChange={(e) => setPinned(e.target.checked)}
                            data-testid="note-editor-pinned"
                        />
                        Pin this note to the top
                    </label>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
                    <Button
                        onClick={save}
                        disabled={saving || !body.trim()}
                        className="co-btn-primary"
                        data-testid="note-editor-save"
                    >
                        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                        {mode === "create" ? "Add note" : "Save changes"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function HistoryDialog({ note, onClose }) {
    const revisions = [...(note.revisions || [])].reverse(); // newest first
    return (
        <Dialog open onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="sm:max-w-[640px]" data-testid="note-history-dialog">
                <DialogHeader>
                    <DialogTitle>Edit history</DialogTitle>
                    <DialogDescription>
                        Each entry shows the note's state before that revision.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-3 max-h-[480px] overflow-y-auto">
                    <div className="rounded-md border border-[#E4E4E7] bg-[#FAFAF7] p-3">
                        <div className="text-[11px] uppercase tracking-wide text-[#5E636E]">Current</div>
                        <div className="mt-1 flex items-center gap-2 flex-wrap">
                            <Badge variant="outline" className={`${CATEGORY_STYLES[note.category] || CATEGORY_STYLES.general} border`}>
                                {labelForCategory(note.category)}
                            </Badge>
                            {note.pinned && <Badge className="bg-[#0F1115] text-white border-[#0F1115]">Pinned</Badge>}
                        </div>
                        <p className="mt-2 text-sm whitespace-pre-wrap">{note.body}</p>
                        <div className="mt-2 text-[11px] text-[#5E636E]">
                            Updated by {note.updated_by_name || "—"} on {fmtTime(note.updated_at)}
                        </div>
                    </div>

                    {revisions.length === 0 ? (
                        <div className="text-sm text-[#5E636E]">No prior revisions.</div>
                    ) : (
                        revisions.map((r, idx) => (
                            <div key={idx} className="rounded-md border border-[#E4E4E7] p-3">
                                <div className="text-[11px] uppercase tracking-wide text-[#5E636E]">
                                    Before edit on {fmtTime(r.at)} by {r.by_name || "—"}
                                </div>
                                <div className="mt-1 flex items-center gap-2 flex-wrap">
                                    <Badge variant="outline" className={`${CATEGORY_STYLES[r.prev_category] || CATEGORY_STYLES.general} border`}>
                                        {labelForCategory(r.prev_category)}
                                    </Badge>
                                    {r.prev_pinned && <Badge className="bg-[#0F1115] text-white border-[#0F1115]">Pinned</Badge>}
                                </div>
                                <p className="mt-2 text-sm whitespace-pre-wrap text-[#5E636E]">{r.prev_body}</p>
                            </div>
                        ))
                    )}
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>
                        <X className="h-4 w-4" /> Close
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
